import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { findUnit } from '@/lib/content';
import { lastAttemptStore, sessionStore } from '@/lib/storage';
import { buildQuestions, gradeFor, isCorrect } from '@/lib/quiz';
import type { Attempt, QuizMode } from '@/types';

export default function Results() {
  const { unitId = '' } = useParams();
  const unit = findUnit(unitId);
  const navigate = useNavigate();
  const [attempt] = useState<Attempt | null>(() => (unit ? lastAttemptStore.get(unit.id) : null));

  useEffect(() => {
    sessionStore.clear();
    window.scrollTo({ top: 0 });
  }, []);

  if (!unit) return <Navigate to="/" replace />;
  if (!attempt) return <Navigate to={`/q/${unit.id}`} replace />;

  const { score, total, questions, answers } = attempt;
  const pct = Math.round((score / total) * 100);
  const grade = gradeFor(pct);

  const wrong = questions
    .map((q, i) => ({ q, answer: answers[i] }))
    .filter(({ q, answer }) => !isCorrect(q, answer));

  const isExam = attempt.mode === 'final40';
  const byUnit = isExam
    ? [...questions
        .reduce((acc, q, i) => {
          const label = q.srcUnit ?? 'Other';
          const row = acc.get(label) ?? { label, score: 0, total: 0 };
          row.total += 1;
          if (isCorrect(q, answers[i])) row.score += 1;
          acc.set(label, row);
          return acc;
        }, new Map<string, { label: string; score: number; total: number }>())
        .values()].sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const restart = (mode: QuizMode) => {
    const newQuestions = buildQuestions(unit, mode);
    sessionStore.set({
      unitId: unit.id,
      mode,
      startedAt: Date.now(),
      questions: newQuestions,
      answers: new Array(newQuestions.length).fill(null),
      cur: 0,
      score: 0,
    });
    navigate(`/q/${unit.id}/play`);
  };

  const formatAnswer = (q: typeof questions[number], answer: number[] | null): string => {
    if (!answer || answer.length === 0) return 'No answer';
    return answer.map((i) => q.opts[i]).join(', ');
  };

  return (
    <div className="results">
      <div className="results-circle" style={{ borderColor: grade.color }}>
        <div className="results-pct" style={{ color: grade.color }}>{pct}%</div>
        <div className="results-label">{score} / {total}</div>
      </div>
      <h2>{grade.label}</h2>
      <p className="results-sub">
        {isExam
          ? `${unit.courseCode} · Final Exam · ${total} questions`
          : attempt.mode === 'readingcheck'
            ? `${unit.courseCode} · Unit ${unit.unitNumber} · Reading Check`
            : `${unit.courseCode} · Unit ${unit.unitNumber}: ${unit.unitTitle}`}
      </p>

      {isExam && byUnit.length > 0 && (
        <div className="unit-breakdown">
          {byUnit.map((row) => {
            const rowPct = Math.round((row.score / row.total) * 100);
            return (
              <div key={row.label} className="ub-row">
                <div className="ub-head">
                  <span className="ub-label">{row.label}</span>
                  <span className="ub-score">{row.score}/{row.total} · {rowPct}%</span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{ width: `${rowPct}%`, background: gradeFor(rowPct).color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="results-actions">
        {isExam ? (
          <button className="btn btn-primary" onClick={() => restart('final40')}>
            Retake Final Exam →
          </button>
        ) : attempt.mode === 'readingcheck' ? (
          <button className="btn btn-primary" onClick={() => restart('readingcheck')}>
            Another Reading Check →
          </button>
        ) : (
          <>
            <button className="btn btn-primary" onClick={() => restart('full')}>
              Retake All {unit.questions.length} →
            </button>
            <button className="btn btn-secondary" onClick={() => restart('fit30')}>FIT-Style 30</button>
          </>
        )}
        <Link to={`/q/${unit.id}`} className="btn btn-secondary">
          {isExam ? 'Exam Home' : 'Unit Home'}
        </Link>
        <Link to="/" className="btn btn-secondary">All Courses</Link>
      </div>

      {wrong.length > 0 && (
        <div className="review-section">
          <h3>Review Missed ({wrong.length})</h3>
          {wrong.map(({ q, answer }, i) => (
            <div key={i} className="review-item">
              <div className="ri-q">{q.q}</div>
              <div className="ri-your">Your answer: {formatAnswer(q, answer)}</div>
              <div className="ri-correct">Correct: {q.ans.map((idx) => q.opts[idx]).join(', ')}</div>
              {q.example && <div className="ri-example">💡 {q.example}</div>}
              <div className="ri-source">{q.srcUnit ? `${q.srcUnit} · ${q.source}` : q.source}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
